// src/utils/plannerAggregation.js
//
// Canonical aggregation over the Planner's week amounts.
//
// plannerState stores every expense's entry under TWO keys — the canonical
// `id:<id>` key and a plain-name alias kept for backward compatibility — so
// naively iterating Object.values(plannerState) counts each expense twice.
// (printUtils used to carry a local workaround for exactly this.)
//
// These helpers iterate the actual Monthly/Annual expense records and resolve
// each one's planner entry once, the same way the Planner page renders its
// rows, so every consumer sums the same numbers the user sees.

import { getPlannerKey } from './plannerKeys';

/** Resolve the planner entry for an expense: id key first, name fallback. */
export function getPlannerEntryForExpense(plannerState = {}, expense = {}) {
  const key = getPlannerKey(expense);
  return plannerState[key] || (expense.name ? plannerState[expense.name] : undefined);
}

/**
 * One row per named Monthly/Annual expense with its planner entry and
 * monthly amount (annual amounts are amortized ÷ 12, matching the Planner).
 * @param {Object} data state.data — needs monthly, annual, plannerState.
 * @returns {Array<{ expense, entry, monthlyAmount, isAnnual }>}
 */
export function getCanonicalPlannerRows(data = {}) {
  const plannerState = data.plannerState || {};
  const rows = [];

  const collect = (collection, isAnnual) => {
    Object.entries(collection || {}).forEach(([categoryKey, expenses]) => {
      (Array.isArray(expenses) ? expenses : []).forEach(expense => {
        if (!expense?.name || !expense.name.trim()) return;
        const amount = parseFloat(expense.actual || expense.amount) || 0;
        rows.push({
          expense: { ...expense, categoryKey },
          entry: getPlannerEntryForExpense(plannerState, { ...expense, categoryKey }),
          monthlyAmount: isAnnual ? amount / 12 : amount,
          isAnnual
        });
      });
    });
  };

  collect(data.monthly, false);
  collect(data.annual, true);
  return rows;
}

/** Per-week planned expense totals [w1..w5], each expense counted once. */
export function sumPlannerWeekTotals(data = {}) {
  const totals = [0, 0, 0, 0, 0];
  getCanonicalPlannerRows(data).forEach(({ entry }) => {
    (Array.isArray(entry?.weeks) ? entry.weeks : []).forEach((amount, i) => {
      if (i < 5) totals[i] += parseFloat(amount) || 0;
    });
  });
  return totals;
}

/** Total planned across all weeks (the Planner's "Total Planned Expenses"). */
export function getTotalPlannedExpenses(data = {}) {
  return sumPlannerWeekTotals(data).reduce((sum, v) => sum + v, 0);
}

/**
 * Total monthly commitment the Planner is distributing: monthly actuals plus
 * annual ÷ 12 — i.e. the Monthly tab total plus the annual amortization the
 * header's Total Expenses also uses.
 */
export function getTotalMonthlyCommitment(data = {}) {
  return getCanonicalPlannerRows(data).reduce((sum, row) => sum + row.monthlyAmount, 0);
}
